import { useCallback, useReducer } from 'react';

/** The header drawers are mutually exclusive, so one value describes the whole state. */
export type HeaderPanel = 'none' | 'profile' | 'notifications' | 'help';

/** Any panel other than the closed state. */
export type OpenHeaderPanel = Exclude<HeaderPanel, 'none'>;

export type HeaderPanelAction = { type: 'open'; panel: OpenHeaderPanel } | { type: 'close' };

export interface IHeaderPanelReducerState {
  openPanel: HeaderPanel;
  /**
   * The panel whose content should be rendered. It deliberately lags `openPanel` by keeping
   * the last panel that was open, so the drawer still has something to show while it
   * animates shut instead of collapsing to an empty box on the way out.
   */
  activePanel: OpenHeaderPanel;
}

export const initialHeaderPanelState: IHeaderPanelReducerState = {
  openPanel: 'none',
  activePanel: 'notifications',
};

export const headerPanelReducer = (
  state: IHeaderPanelReducerState,
  action: HeaderPanelAction,
): IHeaderPanelReducerState => {
  switch (action.type) {
    case 'open': {
      // Re-activating the button of the panel that is already open closes it.
      const openPanel = state.openPanel === action.panel ? 'none' : action.panel;
      return {
        openPanel,
        activePanel: openPanel === 'none' ? state.activePanel : openPanel,
      };
    }
    case 'close':
      // Returning the same reference when there is nothing to close stops a redundant
      // `onOpenChange(false)` from the drawer re-rendering the whole header.
      return state.openPanel === 'none' ? state : { ...state, openPanel: 'none' };
    default:
      return state;
  }
};

export interface IHeaderPanelState {
  openPanel: HeaderPanel;
  activePanel: OpenHeaderPanel;
  isOpen: (panel: OpenHeaderPanel) => boolean;
  toggle: (panel: OpenHeaderPanel) => void;
  close: () => void;
}

/**
 * Replaces Coherence's `usePanels`, which identified the open panel by comparing `IPanel`
 * component refs and therefore needed a second piece of state for any panel that was not a
 * Coherence panel. A single discriminated value makes "open one, close the others" true by
 * construction, and keeps the header free of refs.
 */
const useHeaderPanel = (): IHeaderPanelState => {
  const [{ openPanel, activePanel }, dispatch] = useReducer(
    headerPanelReducer,
    initialHeaderPanelState,
  );

  const isOpen = useCallback((panel: OpenHeaderPanel) => openPanel === panel, [openPanel]);

  const toggle = useCallback((panel: OpenHeaderPanel) => {
    dispatch({ type: 'open', panel });
  }, []);

  const close = useCallback(() => {
    dispatch({ type: 'close' });
  }, []);

  return { openPanel, activePanel, isOpen, toggle, close };
};

export default useHeaderPanel;
